import * as GC from '@mescius/spread-sheets';
import { CellTag, LoopId, LoopTag } from '../types';
import { isCellInRange } from './range';

function isCellTag(value: unknown): value is CellTag {
  return typeof value === 'object' && value !== null;
}

function resolveTagCellPosition(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): { row: number; col: number } {
  const spans = sheet.getSpans(undefined, GC.Spread.Sheets.SheetArea.viewport);

  for (const span of spans) {
    if (isCellInRange(row, col, span)) {
      return {
        row: span.row,
        col: span.col,
      };
    }
  }

  return { row, col };
}

export function getCellTagSafely(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): CellTag | undefined {
  const pos = resolveTagCellPosition(sheet, row, col);
  const raw = sheet.getTag(pos.row, pos.col);

  return isCellTag(raw) ? raw : undefined;
}

export function setCellTagSafely(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  tag: CellTag | undefined
): void {
  const pos = resolveTagCellPosition(sheet, row, col);

  if (tag == null) {
    sheet.getCell(pos.row, pos.col).clear(GC.Spread.Sheets.StorageType.tag);
    return;
  }

  sheet.setTag(pos.row, pos.col, tag);
}

export function updateCellTagSafely(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  updater: (prev: CellTag | undefined) => CellTag | undefined
): void {
  const pos = resolveTagCellPosition(sheet, row, col);
  const prev = getCellTagSafely(sheet, pos.row, pos.col);
  const next = updater(prev);

  setCellTagSafely(sheet, pos.row, pos.col, next);
}

export function hasPropertyTag(tag: CellTag | undefined): boolean {
  return tag?.property !== null && tag?.property !== undefined;
}

export function isLoopStartTag(tag: CellTag | undefined): boolean {
  return tag?.loop?.start !== null && tag?.loop?.start !== undefined;
}

export function isLoopEndTag(tag: CellTag | undefined): boolean {
  return tag?.loop?.end !== null && tag?.loop?.end !== undefined;
}

export function isPropertyCell(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): boolean {
  return hasPropertyTag(getCellTagSafely(sheet, row, col));
}

export function isLoopStartCell(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): boolean {
  return isLoopStartTag(getCellTagSafely(sheet, row, col));
}

export function isLoopEndCell(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number
): boolean {
  return isLoopEndTag(getCellTagSafely(sheet, row, col));
}

export function someCellTagInRange(
  sheet: GC.Spread.Sheets.Worksheet,
  range: GC.Spread.Sheets.Range,
  predicate: (tag: CellTag | undefined) => boolean
): boolean {
  const rowEnd = range.row + range.rowCount;
  const colEnd = range.col + range.colCount;

  for (let row = range.row; row < rowEnd; row++) {
    for (let col = range.col; col < colEnd; col++) {
      const tag = getCellTagSafely(sheet, row, col);

      if (predicate(tag)) {
        return true;
      }
    }
  }

  return false;
}

export function hasCellTagInRange(
  sheet: GC.Spread.Sheets.Worksheet,
  range: GC.Spread.Sheets.Range
): boolean {
  return someCellTagInRange(sheet, range, (tag) => tag !== undefined);
}

export function removeLoopFromCellTag(
  tag: CellTag | undefined,
  loopId: LoopId
): CellTag | undefined {
  if (!tag?.loop) return tag;

  const shouldRemoveStart = tag.loop.start === loopId;
  const shouldRemoveEnd = tag.loop.end === loopId;

  if (!shouldRemoveStart && !shouldRemoveEnd) {
    return tag;
  }

  const nextLoop: LoopTag = {
    ...(shouldRemoveStart ? {} : { start: tag.loop.start }),
    ...(shouldRemoveEnd ? {} : { end: tag.loop.end }),
  };

  const hasLoop = nextLoop.start !== undefined || nextLoop.end !== undefined;

  const nextTag: CellTag = {
    ...tag,
    ...(hasLoop ? { loop: nextLoop } : {}),
  };

  if (!hasLoop) {
    delete nextTag.loop;
  }

  if (nextTag.property === undefined && nextTag.loop === undefined) {
    return undefined;
  }

  return nextTag;
}

export function removePropertyFromCellTag(
  tag: CellTag | undefined
): CellTag | undefined {
  if (!tag?.property) return tag;

  const nextTag: CellTag = { ...tag };
  delete nextTag.property;

  if (nextTag.loop === undefined) {
    return undefined;
  }

  return nextTag;
}

export function addLoopStartToCellTag(
  tag: CellTag | undefined,
  loopId: LoopId
): CellTag {
  return {
    ...tag,
    loop: {
      ...tag?.loop,
      start: loopId,
    },
  };
}

export function addLoopEndToCellTag(
  tag: CellTag | undefined,
  loopId: LoopId
): CellTag {
  return {
    ...tag,
    loop: {
      ...tag?.loop,
      end: loopId,
    },
  };
}

export function addLoopHeaderEndToCellTag(
  tag: CellTag | undefined,
  loopId: LoopId
): CellTag {
  return {
    ...tag,
    loop: {
      ...tag?.loop,
      headerEnd: loopId,
    },
  };
}
